'use client'

import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { RootState } from '@store/store'
import {
  updateVerificationCode,
  sendEmailSuccess,
} from '@store/features/passwordRecoverySlice'
import { useRequestPasswordResetMutation } from '@/lib/store/api-helpers/userApi'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPItem,
  toast,
} from 'keep-react'
import { useStyleSchema } from '@components/styleSchema/StyleSchemaHandler'

/**
 * One-Time Password (OTP) input component with timer functionality
 * Allows users to enter verification code and tracks expiration time
 *
 * Features:
 * - 6-digit OTP input field
 * - 15-minute countdown timer
 * - Verification code state management
 * - Resend functionality
 *
 * @returns {JSX.Element} OTP input component with timer and resend button
 */
export interface OTPInputProps {
  email?: string
  onSuccess?: (code: string) => void
  onError?: (msg: string) => void
}

export function OTPInput({ email, onSuccess, onError }: OTPInputProps) {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const [value, setValue] = useState('')
  const [timeLeft, setTimeLeft] = useState<number>(0)
  const [isResending, setIsResending] = useState(false)

  const { currentSchema } = useStyleSchema()
  const buttonColor = currentSchema.colors?.primary?.hex

  const { verificationCode, timestamp } = useSelector(
    (state: RootState) => state.passwordRecovery
  )

  const [otp, setOtp] = useState(verificationCode)
  const [requestPasswordReset] = useRequestPasswordResetMutation()

  useEffect(() => {
    dispatch(updateVerificationCode(otp))
  }, [otp, dispatch])

  // Set up timer based on timestamp
  // Initialisiere einen Standard-Timer beim Mount
  useEffect(() => {
    // Setze Timer immer auf 15 Minuten, selbst wenn kein timestamp existiert
    setTimeLeft(900)
  }, [])

  // Haupttimer basierend auf aktuellem Zeitpunkt
  useEffect(() => {
    // 15 Minuten Ablaufzeit ab jetzt
    const expirationTime = Date.now() + 15 * 60 * 1000

    // Speichere im localStorage
    localStorage.setItem('otpExpirationTime', expirationTime.toString())

    // Timer-Update-Funktion
    const updateTimer = () => {
      const now = Date.now()
      const remaining = Math.max(0, expirationTime - now)
      const remainingSeconds = Math.ceil(remaining / 1000)

      setTimeLeft(remainingSeconds)

      if (remaining <= 0) {
        clearInterval(intervalId)
        localStorage.removeItem('otpExpirationTime')
      }
    }

    // Starte Timer
    updateTimer()
    const intervalId = setInterval(updateTimer, 1000)

    return () => {
      clearInterval(intervalId)
      localStorage.removeItem('otpExpirationTime')
    }
  }, [timestamp])

  // Prüfe auf gespeicherte Ablaufzeit beim Komponenten-Mount
  useEffect(() => {
    const savedExpirationTime = localStorage.getItem('otpExpirationTime')

    if (savedExpirationTime) {
      const expirationTime = parseInt(savedExpirationTime, 10)
      const now = Date.now()
      const remaining = Math.max(0, expirationTime - now)

      if (remaining > 0) {
        // Setze den Timer auf die verbleibende Zeit
        setTimeLeft(Math.ceil(remaining / 1000))

        // Timer starten
        const intervalId = setInterval(() => {
          const currentTime = Date.now()
          const timeRemaining = Math.max(0, expirationTime - currentTime)
          const remainingSeconds = Math.ceil(timeRemaining / 1000)

          setTimeLeft(remainingSeconds)

          if (timeRemaining <= 0) {
            clearInterval(intervalId)
            localStorage.removeItem('otpExpirationTime')
          }
        }, 1000)

        return () => {
          clearInterval(intervalId)
        }
      } else {
        // Ablaufzeit bereits vorbei, entferne aus localStorage
        localStorage.removeItem('otpExpirationTime')
      }
    }
  }, [])

  /**
   * Formats remaining time into MM:SS format for display
   *
   * @param {number | null} seconds - Remaining time in seconds
   * @returns {string} Formatted time string (MM:SS)
   */
  const formatTimeLeft = (seconds: number | null) => {
    if (seconds === null) return ''
    const minutes = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${minutes}:${secs.toString().padStart(2, '0')}`
  }

  // Handle resend code functionality
  const handleResendCode = async () => {
    setIsResending(true)

    try {
      if (!email) {
        toast.error(t('auth.email-not-sent'))
        if (onError) onError(t('auth.email-not-sent'))
        return
      }

      const response = await requestPasswordReset({ mail: email }).unwrap()

      // Check for the actual API response message
      if (
        response.message &&
        (response.message === 'Reset code sent' ||
          response.message.includes('receive a password reset code') ||
          response.message.includes('If your email exists'))
      ) {
        // Update Redux state with a guaranteed new timestamp (avoid duplicate values)
        const guaranteedTimestamp =
          Date.now() + Math.floor(Math.random() * 1000)
        dispatch(sendEmailSuccess({ email, timestamp: guaranteedTimestamp }))
        toast.success(
          t('auth.code-sent-to') + email + t('auth.code-sent-suffix')
        )
      } else {
        toast.error(t('auth.email-not-sent'))
      }
    } catch {
      toast.error(t('auth.email-not-sent'))
    } finally {
      setIsResending(false)
    }
  }

  return (
    <>
      <h2 className='font-bold text-2xl leading-8 tracking-tight text-center'>
        {t('auth.enter-verification-code')}
      </h2>
      <InputOTP
        pasteTransformer={(pasted) => pasted.replaceAll(' ', '')}
        value={value}
        onChange={(value) => {
          setValue(value)
          if (value.length === 6) {
            setOtp(value)
            if (onSuccess) onSuccess(value)
          }
        }}
        className='max-w-full'
        maxLength={6}
        pattern={'^\\d+$'}
      >
        <InputOTPGroup className='gap-2'>
          {[...Array(6)].map((_, index) => (
            <InputOTPItem
              key={index}
              className='max-w-16 max-h-14 border-[#D4D4D8]'
              index={index}
            />
          ))}
        </InputOTPGroup>
      </InputOTP>
      <div className='text-[var(--color-zinc-600)] font-poppins text-sm font-normal leading-6 text-center'>
        {timeLeft > 0 ? (
          <>
            <p>
              {t('auth.code-expiration-time', {
                time: Math.ceil(timeLeft / 60),
              })}
            </p>
            <p className='font-semibold mt-1'>{formatTimeLeft(timeLeft)}</p>
          </>
        ) : (
          <p>{t('auth.code-expired-message')}</p>
        )}
      </div>
      {isResending && <button
        onClick={handleResendCode}
        disabled={Boolean(timeLeft && timeLeft > 0) || isResending}
        className='px-4 py-2 rounded text-center md:w-auto disabled:opacity-50 disabled:cursor-not-allowed'
        style={{ background: buttonColor, color: 'white' }}
        type='button'
        >
          {t('auth.reset-code-sent')}
        </button>
      }
    </>
  )
}
